import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'

import { useSSOV2Store } from '@hhgtech/hhg-components/ssoV2'
import { useSearchParams } from 'next/navigation'
import { useIntl } from 'react-intl'

import { callApiNew } from 'api/axios'
import { PATHS } from 'api/paths'
import { getHelloSitesUrl } from 'api/utils/paths'
import { redirectToSSOWithReturnUrl } from 'api/utils/redirects'
import { showNotification } from 'components/molecules/notificationsHandler'
import { IS_SSOV2_ENABLED } from 'interfaces/constants'
import { CommunityItem } from 'interfaces/types'
import { actionTypes } from 'state/actionTypes'
import { AppContext } from 'state/appContext'

import { useRouterExtend } from './useRouterExtend'

export function useCommunity(props?: { communities?: CommunityItem[] }) {
  const { communities: communitiesProps } = props || {}
  const {
    state: { isLoggedIn },
    dispatch,
  } = useContext(AppContext) || {}
  const debounce = useRef<NodeJS.Timeout>()
  const [loading, setLoading] = useState(false)
  const [communities, setCommunities] = useState<CommunityItem[]>([])

  const getCommunities = useCallback(async () => {
    setLoading(true)
    try {
      const res = await callApiNew(
        PATHS.TOGETHER.GET_TOGETHER_COMMUNITY,
        undefined,
        'together',
      )
      if (res?.data?.communities) {
        return { data: res.data.communities as CommunityItem[], errors: [] }
      }
    } catch (error) {
      console.log('*** useHomeCommunities ERROR :', error)
    } finally {
      setLoading(false)
    }
    return { data: null, errors: [{ errors: ['Somethine went wrong'] }] }
  }, [isLoggedIn])

  useEffect(() => {
    setCommunities(communitiesProps || [])
    const communitiesJoined = (communitiesProps || [])
      .filter(({ joined }: any) => Boolean(joined))
      .map(({ id }: any) => id)
    dispatch({
      type: actionTypes.ADD_COMMUNITY_JOINED,
      payload: {
        communities: communitiesJoined,
      },
    })
  }, [communitiesProps])

  useEffect(() => {
    if (debounce.current) {
      clearTimeout(debounce.current)
    }
    // Cheating
    debounce.current = setTimeout(() => {
      if (!isLoggedIn || loading) {
        return
      }
      getCommunities().then(({ data }) => {
        if (data?.length) {
          setCommunities(data)
          const communitiesJoined = data
            .filter(({ joined }: any) => Boolean(joined))
            .map(({ id }: any) => id)

          dispatch({
            type: actionTypes.ADD_COMMUNITY_JOINED,
            payload: {
              communities: communitiesJoined,
            },
          })
        }
      })
    }, 250)
  }, [getCommunities])

  return useMemo(
    () => ({
      loading,
      setLoading,
      communities,
      setCommunities,
      getCommunities,
    }),
    [loading, communities, getCommunities],
  )
}

export function useJoinCommunity(props: { communities: CommunityItem[] }) {
  const { communities } = props
  const {
    state: { communityJoined = [], isLoggedIn },
    dispatch,
  } = useContext(AppContext) || {}
  const { locale, router } = useRouterExtend()
  const { formatMessage: f } = useIntl()
  const searchParams = useSearchParams()
  const sourceSSOV2 = useSSOV2Store.getState()?.source
  const [loading, setLoading] = useState<Record<string, boolean>>({})

  const onJoinCommunity = useCallback(
    (community: CommunityItem) => {
      return () => {
        const id = community.id
        if (!isLoggedIn) {
          if (IS_SSOV2_ENABLED) {
            useSSOV2Store.getState().triggerLogin({
              source: `community,${id}`,
            })
          } else {
            redirectToSSOWithReturnUrl(
              `${getHelloSitesUrl(locale)}/community/on-boarding/?returnUrl=/${
                community?.slug
              }`,
              'communities',
              'embeddedarticle',
              community?.slug,
            )
          }
          return
        }

        if (community && community.joined) {
          router.replace('', '', { shallow: true })
          useSSOV2Store.setState({ source: undefined })
          window.location.href = `/community/${community?.slug}`
          return
        }

        setLoading((prevLoading) => ({
          ...prevLoading,
          [id]: true,
        }))
        callApiNew(
          PATHS.TOGETHER.POST_TOGETHER_JOIN_COMMUNITY,
          { method: 'POST', data: { ids: [id] } },
          'together',
        )
          .then((res) => {
            if (res?.status === 1) {
              showNotification({
                title: '',
                message: f(
                  {
                    id: 'notification.community.joinCommunitySuccess',
                    defaultMessage: 'You joined {name} community',
                  },
                  { name: community?.name || 'a' },
                ),
                type: 'success',
              })
              dispatch({
                type: actionTypes.ADD_COMMUNITY_JOINED,
                payload: {
                  communityId: id,
                },
              })
              router.replace('', '', { shallow: true })
              useSSOV2Store.setState({ source: undefined })
            }
          })
          .finally(() => {
            setLoading((prevLoading) => ({
              ...prevLoading,
              [id]: false,
            }))
          })
      }
    },
    [isLoggedIn, useSSOV2Store, router, communityJoined],
  )

  useEffect(() => {
    const source = sourceSSOV2 || searchParams.get('source')
    if (typeof source === 'string' && source.includes('community')) {
      const communityId = source.split(',')?.[1]
      const community = communities.find(
        ({ id }) => id === parseInt(communityId),
      )
      if (community) {
        onJoinCommunity(community)()
      }
    }
  }, [communities, sourceSSOV2, searchParams.get('source')])

  return useMemo(
    () => ({
      communityJoined,
      loading,
      setLoading,
      onJoinCommunity,
    }),
    [communityJoined, loading, onJoinCommunity],
  )
}
